<script setup lang="ts">
import type { ITagFilter } from "~/hooks/tags/use-tag-filters";
import useEventStore from "~/stores/event";

export interface BaseEventsTagsProps {
  searchValue: string;
  filters: ITagFilter[];
  activeFilters: Map<ITagFilter["id"], ITagFilter>;
  isAllTagsSelected: boolean;
  checkedTagsTotal?: number;
}
const { t } = useI18n();
const eventStore = useEventStore();

const emit = defineEmits(["update:searchValue", "update:activeFilters", "update:isAllTagsSelected", "clear-all"]);
const props = withDefaults(defineProps<BaseEventsTagsProps>(), {});
const searchValue = useVModel(props, "searchValue", emit);
const isAllTagsSelected = useVModel(props, "isAllTagsSelected", emit);
const innerActiveFilters = reactive(new Map()) as Map<string, ITagFilter>;
const activeInnerFiltersCount = computed(() => innerActiveFilters.size);
const searchInput = ref();
const isSearchView = ref(false);
const typeFilters = computed(() => props.filters.filter(f => f.type === "tagType"));
const playerFilters = computed(() => props.filters.filter(f => f.type === "teamColor-playerNumber"));
const teamFilters = computed(() => props.filters.filter(f => f.type === "tagTeam"));
const isFilterActive = (filter: ITagFilter) => innerActiveFilters.has(filter.id);
const onFilterClick = (filter: ITagFilter) => {
  if (isFilterActive(filter))
    innerActiveFilters.delete(filter.id);
  else
    innerActiveFilters.set(filter.id, filter);
};
const applyFilters = (close: () => void) => {
  emit("update:activeFilters", new Map(innerActiveFilters));
  close();
};
const clearFilters = () => {
  innerActiveFilters.clear();
};
const showSearchView = () => {
  isSearchView.value = true;
  nextTick(() => searchInput.value?.focus());
};
const hideSearchView = () => {
  isSearchView.value = false;
  searchValue.value = "";
};

watch(eventStore.tags.data, () => {
  clearFilters();
  applyFilters(close);
});
</script>

<template>
  <div class="h-[60px]">
    <div class="flex justify-between items-center p-4 eventTagsHead">
      <div v-if="isSearchView" class="flex w-full justify-start items-center">
        <IconSearch class="text-secondary mr-2" />
        <BaseInput ref="searchInput" v-model="searchValue" name="search" :placeholder="t('pages.events.tabs.event_tags.filters.search_input_placeholder')" />
      </div>
      <div v-else>
        <BaseCheckbox
          v-model="isAllTagsSelected"
          class="text-secondary !gap-4"
          name="tags-filters-select-all"
          :label="t('pages.events.tabs.event_tags.filters.select_all', { count: props.checkedTagsTotal })"
        />
      </div>
      <div class="flex gap-8">
        <IconSearch
          v-if="!isSearchView"
          v-tooltip.bottom="t('labels.search')"
          class="text-secondary cursor-pointer"
          :width="20"
          :height="20"
          @click="showSearchView()"
        />
        <IconClose v-else fill="var(--base-icon-color)" class="cursor-pointer" @click="hideSearchView" />
        <EventTagsPlayAll />
        <BasePopover
          content-class="bg-[var(--base-tab-filter-tag-popup-bgColor)] shadow-main right-4 rounded-main rtl:left-0 rtl:right-auto"
          placement="bottom"
        >
          <template #default="{ toggle }">
            <BaseButton v-tooltip.bottom="t('pages.events.tabs.event_tags.filters.popup_title')" name="Filter" type="icon" @click="toggle">
              <BaseBadge :text="activeInnerFiltersCount">
                <IconFilter class="cursor-pointer" fill="var(--base-icon-color)" />
              </BaseBadge>
            </BaseButton>
          </template>
          <template #content="{ close }">
            <div class="p-4 w-[416px] max-md:w-[316px]">
              <div class="mb-6 flex justify-between items-center">
                <h3 class="font-bold">
                  {{ t("pages.events.tabs.event_tags.filters.popup_title") }}
                </h3>
                <IconClose fill="var(--base-icon-color)" class="cursor-pointer" @click="close" />
              </div>

              <!-- TODO: Convert filter group elements to components -->

              <div class="grid gap-6">
                <div v-if="typeFilters.length" class="grid gap-4">
                  <h3 class="text-secondary">
                    {{ t("pages.events.tabs.event_tags.filters.by_type") }}
                  </h3>

                  <div class="flex flex-wrap gap-1">
                    <EventTagsSelect
                      v-for="filter in typeFilters"
                      :key="filter.id"
                      :tag-type="filter.metadata.tagType"
                      :tag-title="t(`tags.${filter.metadata.tagType}`)"
                      :is-active="isFilterActive(filter)"
                      @click="onFilterClick(filter)"
                    />
                  </div>
                </div>

                <div v-if="teamFilters.length" class="grid gap-2 w-full">
                  <h3 class="text-secondary">
                    {{ t("pages.events.tabs.event_tags.filters.by_team") }}
                  </h3>
                  <div class="flex gap-2 flex-col">
                    <div
                      v-for="filter in teamFilters"
                      :key="filter.id"
                      class="rounded p-2 border items-center cursor-pointer"
                      :style="{ borderColor: isFilterActive(filter) ? 'var(--base-primary-color)' : 'var(--base-card-bgSecondaryColor)' }"
                      @click="onFilterClick(filter)"
                    >
                      <div class="flex w-[350px] items-center gap-2">
                        <div>
                          <BaseLogo
                            :width="24"
                            :height="24"
                            :src="filter.metadata.tagTeam?.logo"
                            :icon-background="filter.metadata.tagTeam?.color"
                            :title="filter.metadata.tagTeam?.title"
                            with-initials
                            with-hover
                          />
                        </div>
                        <p class="truncate">
                          {{ filter.metadata.tagTeam?.title }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="playerFilters.length" class="grid gap-4">
                  <h3 class="text-secondary">
                    {{ t("pages.events.tabs.event_tags.filters.by_player_number") }}
                  </h3>
                  <div class="flex flex-wrap gap-4">
                    <div
                      v-for="(filter, index) in playerFilters" :key="index"
                      class="flex gap-2 items-center rounded p-2  border  cursor-pointer"
                      :style="{ borderColor: isFilterActive(filter) ? 'var(--base-primary-color)' : 'var(--base-card-bgSecondaryColor)' }"
                      @click="onFilterClick(filter)"
                    >
                      <IconTshirt :fill="filter.metadata.tagTeam?.color" />
                      <span>
                        # {{ filter.metadata.tagPlayerNumber }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="w-full flex justify-between mt-6 gap-2">
                <BaseButton
                  type="secondary"
                  name="modal-close"
                  :disabled="!Boolean(activeInnerFiltersCount)"
                  :title="t('button.clear_all')"
                  class="text-button-bold text-main w-1/2 !px-0"
                  @click="clearFilters"
                >
                  {{ t("pages.events.tabs.event_tags.filters.clear_all_btn", { count: activeInnerFiltersCount }) }}
                </BaseButton>
                <BaseButton
                  type="danger"
                  name="create-clip"
                  :title="t('pages.events.tabs.event_tags.filters.show_results_btn')"
                  class="text-button-bold text-main w-1/2 !px-0"
                  @click="applyFilters(close)"
                >
                  {{ t("pages.events.tabs.event_tags.filters.show_results_btn") }}
                </BaseButton>
              </div>
            </div>
          </template>
        </BasePopover>
      </div>
    </div>
    <div v-if="isSearchView" class="hr" role="separator" style="background: var(--base-background-color)" />
  </div>
</template>

<style lang="scss">
.eventTagsHead {
  .baseInput .baseInput__inner {
    @apply p-0 border-0;
  }
  .basePopover {
    position: inherit;
    &:focus-within .badge path {
      fill: var(--base-text-color);
    }
  }
}
</style>
